import React, { useEffect, useState } from 'react'
import { ClockingType, IClockingAction } from '@cbgms/api/modules/clocking-actions'
import { IBaseClockingItem } from '@cbgms/api/modules/clocking-items'
import { useTranslation } from 'react-i18next'
import { Grid, Header, Icon, Label, Button as SUIButton } from 'semantic-ui-react'
import LicensePlate from '@cbgms/components/form/LicensePlate'
import { apiFormat, format, formatters } from '@cbgms/base/utils/datetime'
import TimeActions from './TimeActions'
import N from 'utils/navigation'
import Timer from './Timer'
import VehicleMakeImage from 'app/vehicles/components/DashboardDetails/VehicleMakeImage.Semantic'
import { Button, IconButton, Stack, Tooltip } from '@mui/material'
import TodayIcon from '@mui/icons-material/Today'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import moment from 'moment'
import { useFeature } from '@cbgms/base/feature-flags'
import '../../assets/styles/components/scrollbar.less'
import StopIcon from '@mui/icons-material/Stop'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'

export interface ITimeActionsOverviewProps {
  stopDay: () => void
  currentAction: IClockingAction | null
  dayAction: IClockingAction
  clockingItems: IBaseClockingItem[]
  workorderItems: IBaseClockingItem[]
  workorderClockingItems: IBaseClockingItem[]
  clockIn: (type: ClockingType, typeUUID?: string, workorderClockingItemUUID?: string) => void
  clockOut: () => void
  employeeUUID: string
}

const TimeActionsOverview: React.FC<ITimeActionsOverviewProps> = ({
  stopDay,
  clockIn,
  clockOut,
  currentAction,
  dayAction,
  clockingItems,
  workorderItems,
  workorderClockingItems
}) => {
  const { t } = useTranslation()
  const [current, setCurrent] = useState('today')
  const [filteredWorkorders, setFilteredWorkorders] = useState(workorderItems)
  const isOnBreak = !!currentAction && currentAction.Type === ClockingType.Break
  const hasAllOrdersinTimeRegistration = useFeature('orders-in-time-registration')

  const onClickPast = () => {
    setCurrent('past')
    const workorderByDates = workorderItems.filter(workorder =>
      moment(workorder.AppointmentDate).isBefore(moment(moment().format(apiFormat.yearMonthDay)))
    )
    setFilteredWorkorders(workorderByDates)
  }

  const onClickToday = () => {
    setCurrent('today')
    const workorderByDates = workorderItems.filter(workorder => workorder.AppointmentDate === moment().format(apiFormat.yearMonthDay))
    setFilteredWorkorders(workorderByDates)
  }

  const onClickNext = () => {
    setCurrent('next')
    const workorderByDates = workorderItems.filter(workorder =>
      moment(workorder.AppointmentDate).isAfter(moment(moment().format(apiFormat.yearMonthDay)))
    )
    setFilteredWorkorders(workorderByDates)
  }

  useEffect(() => {
    const workorderByDates = workorderItems.filter(workorder => workorder.AppointmentDate === moment().format(apiFormat.yearMonthDay))
    setFilteredWorkorders(workorderByDates)
  }, [])

  return (
    <Grid>
      {hasAllOrdersinTimeRegistration ? (
        <Grid.Row columns='2' style={{ borderBottom: '1px solid #efefef', paddingBottom: '10px', marginBottom: '5px' }}>
          <Grid.Column floated='left' divided>
            {t('TimeRegistration.StartedDay')}
            <Label className='ml-half' style={{ fontSize: '0.9rem' }}>
              <Icon name='clock' /> {formatters.datetime.fromApiFormat(dayAction.ClockedInAt).format(format.time)}
            </Label>
          </Grid.Column>
          <Grid.Column className='eight wide' floated='right' style={{ display: 'flex', alignItems: 'end', justifyContent: 'end' }}>
            <Button
              data-testid='timeregistration-stopday'
              variant='contained'
              size='small'
              onClick={stopDay}
              startIcon={<StopIcon fontSize='small' />}
            >
              {t('TimeRegistration.StopDay')}
            </Button>
          </Grid.Column>
        </Grid.Row>
      ) : (
        <Grid.Row>
          <Grid.Column>
            {t('TimeRegistration.StartedDay')}
            <Label className='ml-half'>
              <Icon name='clock' /> {formatters.datetime.fromApiFormat(dayAction.ClockedInAt).format(format.time)}
            </Label>
            <SUIButton data-testid='timeregistration-stopday' size='mini' icon floated='right' basic onClick={stopDay}>
              <Icon name='stop' /> {t('TimeRegistration.StopDay')}
            </SUIButton>
          </Grid.Column>
        </Grid.Row>
      )}
      <Grid.Row columns='2' divided>
        {hasAllOrdersinTimeRegistration ? (
          <Grid.Column className='seven wide'>
            <Stack marginBottom={1.5} marginTop={1}>
              <Header as='h3' style={{ marginBottom: 0 }}>
                {t('TimeRegistration.ItemsTitle')}
              </Header>
            </Stack>
            <Stack className='thinScrollbar' sx={{ maxHeight: '430px', overflowY: 'auto' }}>
              <TimeActions
                id='items'
                items={clockingItems}
                currentAction={currentAction}
                clockIn={clockingItemUUID => clockIn(ClockingType.Item, clockingItemUUID)}
                clockOut={clockOut}
              />
            </Stack>
            {isOnBreak ? (
              <Button
                data-testid='timeregistration-stopbreak'
                fullWidth
                sx={{ position: 'relative', marginTop: 1.5 }}
                size='medium'
                onClick={() => clockOut()}
                startIcon={<StopIcon />}
                variant='outlined'
              >
                {t('TimeRegistration.StopBreak')}
                <Label data-testid='timeregistration-breaktime' basic style={{ position: 'absolute', top: '4px', right: '4px' }}>
                  <Timer workedTime={currentAction!.TotalClockedSeconds} isInActions />
                </Label>
              </Button>
            ) : (
              <Button
                data-testid='timeregistration-startbreak'
                size='medium'
                sx={{ position: 'relative', marginTop: 1.5 }}
                fullWidth
                onClick={() => clockIn(ClockingType.Break)}
                startIcon={<PlayArrowIcon />}
                variant='outlined'
              >
                {t('TimeRegistration.StartBreak')}
              </Button>
            )}
          </Grid.Column>
        ) : (
          <Grid.Column>
            <Header as='h3'>{t('TimeRegistration.ItemsTitle')}</Header>
            <TimeActions
              id='items'
              items={clockingItems}
              currentAction={currentAction}
              clockIn={clockingItemUUID => clockIn(ClockingType.Item, clockingItemUUID)}
              clockOut={clockOut}
            />
          </Grid.Column>
        )}
        <Grid.Column className={hasAllOrdersinTimeRegistration ? 'nine wide' : ''}>
          {hasAllOrdersinTimeRegistration ? (
            <>
              <Stack flexDirection='row' justifyContent='space-between' alignItems='center' marginBottom={1.5}>
                <Header as='h3' style={{ marginBottom: 0 }}>
                  {t('TimeRegistration.YourWorkorders')} ({filteredWorkorders.length})
                </Header>
                <Stack flexDirection='row' justifyContent='flex-end' gap={0.5}>
                  <Tooltip title={t('TimeRegistration.PastSevenDays')}>
                    <IconButton disabled={current === 'past'} disableRipple onClick={onClickPast}>
                      <ChevronLeftIcon sx={{ color: current === 'past' ? '#00000042' : '#000000' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('TimeRegistration.Today')}>
                    <IconButton disableRipple onClick={onClickToday}>
                      <TodayIcon sx={{ color: '#000000' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('TimeRegistration.NextSevenDays')}>
                    <IconButton disabled={current === 'next'} disableRipple onClick={onClickNext}>
                      <ChevronRightIcon sx={{ color: current === 'next' ? '#00000042' : '#000000' }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              <Stack className='thinScrollbar' sx={{ maxHeight: '430px', overflowY: 'auto', paddingRight: '10px' }}>
                <TimeActions
                  id='workorders'
                  items={filteredWorkorders}
                  workorderClockingItems={workorderClockingItems}
                  currentAction={currentAction}
                  clockIn={(workorderUUID, workorderClockingItemUUID) =>
                    clockIn(ClockingType.Workorder, workorderUUID, workorderClockingItemUUID)
                  }
                  clockOut={clockOut}
                  NameComponent={LicensePlateName}
                  viewingWorkorder={current}
                />
              </Stack>
            </>
          ) : (
            <>
              <Header as='h3'>{t('TimeRegistration.WorkordersTitle')}</Header>
              <TimeActions
                id='workorders'
                items={filteredWorkorders}
                currentAction={currentAction}
                clockIn={workorderUUID => clockIn(ClockingType.Workorder, workorderUUID)}
                clockOut={clockOut}
                NameComponent={LicensePlateName}
                viewingWorkorder={current}
              />
            </>
          )}
        </Grid.Column>
        {!hasAllOrdersinTimeRegistration && (
          <>
            <Grid.Column>
              {isOnBreak && (
                <SUIButton
                  data-testid='timeregistration-stopbreak'
                  icon
                  fluid
                  floated='right'
                  style={{ position: 'relative' }}
                  size='medium'
                  onClick={() => clockOut()}
                >
                  <Icon name='stop' /> {t('TimeRegistration.StopBreak')}
                  <Label data-testid='timeregistration-breaktime' basic style={{ position: 'absolute', top: '4px', right: '4px' }}>
                    <Timer workedTime={currentAction!.TotalClockedSeconds} isInActions />
                  </Label>
                </SUIButton>
              )}
              {!isOnBreak && (
                <SUIButton
                  data-testid='timeregistration-startbreak'
                  size='medium'
                  icon
                  floated='right'
                  fluid
                  onClick={() => clockIn(ClockingType.Break)}
                >
                  <Icon name='play' /> {t('TimeRegistration.StartBreak')}
                </SUIButton>
              )}
            </Grid.Column>
            <Grid.Column />
          </>
        )}
      </Grid.Row>
    </Grid>
  )
}

const LicensePlateName: React.FC<{ name: string; url?: string; brand?: string }> = ({ name, url, brand }) => {
  return (
    <div onClick={() => url && N.push(url)} className='clickable'>
      {name.indexOf('|') === 2 ? (
        <Stack alignItems='center' flexDirection='row'>
          <VehicleMakeImage manufacturer={brand} size='small' /> <LicensePlate className='ml-half' size='small' registration={name} />
        </Stack>
      ) : (
        name
      )}
    </div>
  )
}

export default TimeActionsOverview
